import { registerWeekly, type CampaignLevel } from './levels';

/**
 * Weekly Challenge (D123): every player gets the same map and the same horde
 * schedule for a week (UTC, ISO week), one scored attempt, one leaderboard.
 * The level is GENERATED from the week id, so client and server agree without
 * storing anything, and next week is a new puzzle.
 */
export const WEEKLY_LEVEL_ID = 1000;

/** ISO-8601 week id for a UTC time, e.g. "2026-W45". */
export function weekId(ms: number): string {
  const d = new Date(ms);
  const day = (d.getUTCDay() + 6) % 7; // Monday = 0
  const thursday = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day + 3);
  const year = new Date(thursday).getUTCFullYear();
  const jan4 = Date.UTC(year, 0, 4);
  const jan4Day = (new Date(jan4).getUTCDay() + 6) % 7;
  const week1Monday = jan4 - jan4Day * 86_400_000;
  const week = Math.floor((thursday - week1Monday) / (7 * 86_400_000)) + 1;
  return `${year}-W${String(week).padStart(2, '0')}`;
}

export function isWeekId(v: unknown): v is string {
  return typeof v === 'string' && /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/.test(v);
}

/** Milliseconds until the next Monday 00:00 UTC. */
export function msToNextWeek(ms: number): number {
  const d = new Date(ms);
  const day = (d.getUTCDay() + 6) % 7;
  const nextMonday = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day + 7);
  return nextMonday - ms;
}

function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

const TWISTS = [
  { name: 'Scrap Drought', brief: 'Barely any scrap to start with. Towers will cost you.', res: { wood: 240, scrap: 30, food: 140 }, runners: 0.2 },
  { name: 'Fast Week', brief: 'Runners from the very first horde.', res: { wood: 200, scrap: 90, food: 130 }, runners: 0.4 },
  { name: 'Crowded Valley', brief: 'The infected already fill the middle of the map.', res: { wood: 200, scrap: 90, food: 130 }, runners: 0.25, crowded: true },
  { name: 'Lean Times', brief: 'Little food and little time between hordes.', res: { wood: 180, scrap: 80, food: 70 }, runners: 0.25, quick: true },
  { name: 'Iron Week', brief: 'Plenty of scrap, but the hordes grow fast.', res: { wood: 160, scrap: 200, food: 120 }, runners: 0.3, growth: 2 },
] as const;

/** This week's challenge, generated from its id. */
export function weeklyLevel(week: string): CampaignLevel {
  const h = hash(week);
  const t = TWISTS[h % TWISTS.length]!;
  const size = 60 + ((h >>> 4) % 4) * 4;
  const quick = 'quick' in t && t.quick;
  const crowded = 'crowded' in t && t.crowded;
  const growth = 4 + ('growth' in t ? t.growth : 0);
  return {
    id: WEEKLY_LEVEL_ID,
    name: `Weekly ${week} · ${t.name}`,
    brief: t.brief,
    difficulty: 3,
    map: { seed: 10_000 + (h % 90_000), width: size, height: size },
    startingResources: { ...t.res },
    startingSurvivors: 8,
    startingUnits: { ranger: 4, heavy: 1, engineer: 1 },
    infected: { initial: crowded ? 50 : 20, waves: true, firstWaveSeconds: quick ? 180 : 220, waveEverySeconds: quick ? 85 : 105, waveSize: 9, waveGrowth: growth, maxWaveSize: 45, runnersFromWave: 0, runnerShare: t.runners, specialsFromWave: 2 },
    waves: 5,
    holdSeconds: 90,
  };
}

registerWeekly(weeklyLevel);
